/**
 * The `kpi_row` visual adapter (SPEC §9.4).
 *
 * Not a chart — a row of tiles, one per series, so it renders directly rather
 * than through ECharts.
 *
 * **How a tile reduces a column to one figure**, which the manifest decides
 * and this component only obeys:
 *
 * * `ColumnSpec.total` of `sum` or `avg` → that aggregate over every row.
 * * `total: "none"` → the **last** row's value.
 *
 * The second rule is the one worth knowing. A running total's meaningful
 * reading is where it ends up, and summing a column of running totals would
 * produce a number that means nothing at all. Declaring `total` on the column
 * is how a report author chooses.
 */

import { kpiTiles } from '../../lib/kpi'
import type { Cell, ColumnSpec, VisualSpec } from '../../lib/types'

export function KpiRow({
  visual,
  columns,
  rows,
}: {
  visual: VisualSpec
  columns: readonly ColumnSpec[]
  rows: readonly (readonly Cell[])[]
}) {
  const tiles = kpiTiles(visual, columns, rows)

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          title={tile.basis}
          className="rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-[var(--color-surface-raised)] p-3 elevated"
        >
          {/* The accent hairline is the department wayfinding, at tile scale. */}
          <div aria-hidden className="mb-2 h-0.5 w-6 rounded-full bg-[var(--accent)]" />
          <p className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">{tile.label}</p>
          <p className="numeric mt-1 text-[length:var(--text-xl)] font-medium tabular-nums">
            {tile.value}
          </p>
          <p className="mt-0.5 text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
            {tile.basis}
          </p>
        </div>
      ))}
    </div>
  )
}
